import {
  hotelPhotoAlt,
  hotelPhotoUrl,
  ratingBand,
  ratingOrUndefined,
  type HotelDetailsCancellationView,
  type HotelDetailsCopy,
  type HotelDetailsPanelProps,
  type HotelDetailsReviewView,
  type HotelDetailsView,
  type HotelRatingView,
} from '@voll-intelligence/intelligence-ui-kit';

import {
  htmlToPlainText,
  localizedBoardLabel,
  hotelFreeCancellationUntil,
  type HotelRoomsBlock,
  representativeRate,
  type HotelDetailsReview,
  type ChatUiHotelOffer,
  type ChatUiHotelRoom,
  type HotelPlannerPlace,
} from '@voll-intelligence/genui-contract';
import { fmtDayMonth, fmtMoney, localDayIso } from '../../../shared/ui/flight_format.js';
import { genUiT, resolveGenUiLocale } from '../../../shared/ui/i18n.js';
import { hotelChannelName } from '../../shared/channel_brand.js';
import { createHotelRoomsCopy, createHotelRoomsView } from '../../shared/hotel_rooms_view.js';
import { hotelAmenityViews, hotelTravelLabels } from '../../shared/hotel_offer_view.js';
import type { HotelDetailsState } from './use_hotel_details.js';

type HotelDetailsAdapterView = Omit<
  HotelDetailsPanelProps,
  'onClose' | 'onRateSelect' | 'onRoomDetailsOpen' | 'readOnly' | 'busy'
>;

type HotelDetailsViewOptions = {
  locale?: string;
  proxyImageUrl?: (url: string) => string;
  anchor?: HotelPlannerPlace;
  mapboxAccessToken?: string;
  /** O "hoje" (YYYY-MM-DD, dia local) contra o qual a política de cancelamento
   *  é lida; injetável pelos testes, `localDayIso()` por padrão. */
  todayIso?: string;
};

type ReadyHotelOptions = {
  locale: string;
  proxyImageUrl: (url: string) => string;
  anchor?: HotelPlannerPlace;
  mapboxAccessToken?: string;
  todayIso?: string;
};

function detailsCopy(locale: string): HotelDetailsCopy {
  const t = (key: Parameters<typeof genUiT>[1]) => genUiT(locale, key);
  return {
    closeLabel: t('hotelDetails.close'),
    previousPhotoLabel: t('hotel.photoPrev'),
    nextPhotoLabel: t('hotel.photoNext'),
    overviewTabLabel: t('hotelDetails.summary'),
    roomsTabLabel: t('hotelDetails.roomsTab'),
    aboutTabLabel: t('hotelDetails.about'),
    sectionsNavigationLabel: t('hotelDetails.sectionsNavigation'),
    previousTabsLabel: t('hotelDetails.previousTabs'),
    nextTabsLabel: t('hotelDetails.nextTabs'),
    readMoreLabel: t('hotelDetails.readMore'),
    readLessLabel: t('hotelDetails.readLess'),
    allAmenitiesLabel: t('hotelDetails.allAmenities'),
    fewerAmenitiesLabel: t('hotelDetails.fewerAmenities'),
    reviewsTabLabel: t('hotelDetails.reviews'),
    locationTabLabel: t('hotelDetails.location'),
    cancellationTabLabel: t('hotelDetails.cancellation'),
    loadingLabel: t('hotelDetails.loading'),
    errorTitleLabel: t('hotelDetails.errorTitle'),
    retryLabel: t('hotelDetails.retry'),
    roomsEmptyLabel: t('hotelDetails.roomsEmpty'),
  };
}

function ratingLabel(value: number, locale: string): string {
  const shown = value.toLocaleString(resolveGenUiLocale(locale), {
    maximumFractionDigits: 1,
  });
  return `${genUiT(locale, 'hotel.rating', { rating: shown })} · ${genUiT(
    locale,
    `hotelRooms.ratingLabel.${ratingBand(value)}`,
  )}`;
}

function reviewViews(reviews: HotelDetailsReview[], locale: string): HotelDetailsReviewView[] {
  return reviews.map((review, index) => ({
    id: `review-${String(index + 1)}`,
    author: review.authorName,
    ...(review.rating === undefined ? {} : { ratingLabel: ratingLabel(review.rating, locale) }),
    text: review.text ?? '',
    ...(review.relativeTimeDescription ? { dateLabel: review.relativeTimeDescription } : {}),
  }));
}

function hotelRating(
  rating: number | undefined,
  reviewCount: number | undefined,
  locale: string,
): HotelRatingView | undefined {
  const value = ratingOrUndefined(rating);
  if (value === undefined) return undefined;
  const shown = value.toLocaleString(resolveGenUiLocale(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const band = genUiT(locale, `hotelRooms.ratingLabel.${ratingBand(value)}`);
  return {
    /* Aqui o `label` é texto VISÍVEL do cabeçalho (`hui-hotel-details-rating`),
     *  não nome acessível: a contagem por extenso não entra, senão o cabeçalho
     *  ganha uma frase que o design não pediu. Na lista de quartos é o oposto —
     *  lá o `label` vira `aria-label` e precisa do substantivo. */
    label: `★ ${shown} ${band}`,
    /* As PARTES da mesma linha, para a Página do Hotel compor peça a peça
     *  (★ + nota em negrito + faixa + contagem apagada — protótipo 2026-08-27). */
    valueLabel: shown,
    bandLabel: band,
    /* O lastro da nota entre parênteses é pontuação numérica, igual nas três
     *  línguas — por isso sem chave de i18n. */
    ...(reviewCount === undefined
      ? {}
      : { reviewsLabel: `(${reviewCount.toLocaleString(resolveGenUiLocale(locale))})` }),
  };
}

/** As fotos da página: as do DETALHE primeiro (o conjunto curado do
 *  fornecedor) e, atrás delas, as que a OFERTA da lista já carregava (carrossel
 *  do card) e a miniatura — dedupe por URL. Sem o merge, um detalhe que volta
 *  com uma foto só jogava fora o carrossel inteiro que o card acabou de
 *  mostrar, e o mosaico do protótipo virava uma foto solitária. */
function hotelPhotos(
  offer: ChatUiHotelOffer,
  detailPhotos: readonly { url: string; description?: string }[],
): { url: string; description?: string }[] {
  const merged = [
    ...detailPhotos,
    ...(offer.photos ?? []),
    ...(offer.thumbnailUrl ? [{ url: offer.thumbnailUrl }] : []),
  ];
  const seen = new Set<string>();
  return merged.filter((photo) => {
    if (seen.has(photo.url)) return false;
    seen.add(photo.url);
    return true;
  });
}

function hotelText(
  offer: ChatUiHotelOffer,
  description: string | undefined,
): Pick<HotelDetailsView, 'summary' | 'description'> {
  /* A prosa do hotel vem crua do fornecedor, com marcação (#2758), e a seção
   *  'Sobre' a renderiza como texto puro — a conversão é aqui, na leitura, a
   *  única camada que converte (pega também o detalhe já em cache). */
  const plain = htmlToPlainText(description);
  return {
    ...(offer.summary ? { summary: offer.summary } : {}),
    ...(plain ? { description: plain } : {}),
  };
}

/** Sem data não há faixa: a janela não tem onde pousar na linha do tempo e o
 *  rótulo sairia pendurado ("Até "). O passthrough do fornecedor não garante as
 *  datas, então a peça some em vez de mentir pela metade — o título, que vem da
 *  derivação, continua dizendo que há multa. */
function windowLabel(
  window: NonNullable<ChatUiHotelRoom['cancellationPolicies']>[number],
  locale: string,
): string | undefined {
  // Com as duas pontas, o ano (fora do ano corrente) vai só no `until`.
  const both = window.from && window.until ? [window.from, window.until] : undefined;
  const from = window.from
    ? fmtDayMonth(window.from, locale, both ? [] : [window.from])
    : undefined;
  const until = window.until
    ? fmtDayMonth(window.until, locale, both ?? [window.until])
    : undefined;
  if (from && until) return genUiT(locale, 'hotelDetails.cancellationBetween', { from, until });
  if (from) return genUiT(locale, 'hotelDetails.cancellationFrom', { date: from });
  return until ? genUiT(locale, 'hotelDetails.cancellationUntil', { date: until }) : undefined;
}

function cancellationDetailLabel(
  penalty: NonNullable<ChatUiHotelRoom['cancellationPolicies']>[number]['penalty'],
  locale: string,
): { detailLabel?: string } {
  if (penalty === undefined) return {};
  /* Os três casos escritos por extenso, sem `else` pegando o resto: o zero é
   * cortesia, o positivo é multa, e QUALQUER outra coisa (negativo de bloco
   * antigo em replay, que o normalizador de hoje já barra na entrada) não
   * vira rótulo nenhum. Um `else` aqui leria lixo como "sem multa". */
  if (penalty.amount === 0) return { detailLabel: genUiT(locale, 'hotelDetails.cancellationFree') };
  return penalty.amount > 0
    ? {
        detailLabel: genUiT(locale, 'hotelDetails.cancellationPenalty', {
          amount: fmtMoney(penalty),
        }),
      }
    : {};
}

/** A política de cancelamento da tarifa representativa, já escrita.
 *
 *  O título afirma o GRÁTIS só quando `hotelFreeCancellationUntil` o sustenta —
 *  a derivação que sabe que a janela do fornecedor é de MULTA, não de cortesia.
 *  Sem data confiável o título vira "com multa" e as faixas falam por si. */
function cancellationView(
  rooms: readonly ChatUiHotelRoom[],
  locale: string,
  todayIso: string = localDayIso(),
): HotelDetailsCancellationView | undefined {
  const rate = representativeRate(rooms);
  const policies = rate?.cancellationPolicies;
  if (!rate || !policies?.length) return undefined;
  const freeUntil = hotelFreeCancellationUntil(policies, rate.refundable);
  const channel = rate.supplier?.name ? hotelChannelName(rate.supplier.name) : undefined;
  const rateLabel = [channel, localizedBoardLabel(rate.boardDescription, locale)]
    .filter(Boolean)
    .join(' · ');
  /* Prazo que JÁ PASSOU não é grátis: a tela dizia "Cancelamento grátis até 15
   * Set" em verde com o calendário no dia 18 (#3217 C6). Datas ISO comparam
   * como texto; a faixa da multa continua abaixo, ela é que explica o custo. */
  const freeStillOpen = freeUntil !== undefined && freeUntil >= todayIso;
  return {
    headline: freeStillOpen
      ? genUiT(locale, 'hotelDetails.cancellationFreeUntil', {
          date: fmtDayMonth(freeUntil, locale),
        })
      : freeUntil
        ? genUiT(locale, 'hotelDetails.cancellationFreeEnded', {
            date: fmtDayMonth(freeUntil, locale),
          })
        : genUiT(locale, 'hotelDetails.cancellationCharged'),
    tone: freeStillOpen ? 'free' : 'charged',
    ...(rateLabel
      ? { rateLabel: genUiT(locale, 'hotelDetails.cancellationForRate', { rate: rateLabel }) }
      : {}),
    windows: policies
      .map((window, index) => {
        const label = windowLabel(window, locale);
        return label
          ? {
              id: `cancellation-${String(index + 1)}`,
              label,
              /* "Sem multa" é AFIRMAÇÃO, e só a multa zero explícita a sustenta
               * — a mesma régua da derivação do título. Multa ausente é dado
               * faltando: a faixa fica só com a data, senão a seção se
               * desmente (título "com multa", faixa "sem multa"). */
              ...cancellationDetailLabel(window.penalty, locale),
            }
          : undefined;
      })
      .filter((window) => window !== undefined),
    ...(rooms.length > 1 ? { noteLabel: genUiT(locale, 'hotelDetails.cancellationVaries') } : {}),
  };
}

function readyHotel(
  offer: ChatUiHotelOffer,
  state: Extract<HotelDetailsState, { phase: 'ready' }>,
  options: ReadyHotelOptions,
): HotelDetailsView {
  const { locale, proxyImageUrl, anchor, mapboxAccessToken } = options;
  const data = state.data;
  const rating = hotelRating(offer.rating, data.reviewCount, locale);
  const cancellation = cancellationView(data.rooms, locale, options.todayIso);
  /* O endereço INTEIRO na linha sob o mapa: o provedor entrega a rua num campo e
   *  a cidade em outro, e ler só `address` deixava "Rua Maranhão, 371" sem
   *  cidade nenhuma (o protótipo escrevia `endereço · bairro`; bairro não existe
   *  no nosso wire). Sem fallback para `offer.title`: nome de hotel na linha de
   *  endereço lê como endereço. Ausente, a seção fica só com mapa e distância. */
  const locationLabel =
    [offer.address, offer.city].filter(Boolean).join(' · ') || anchor?.name || anchor?.city;
  return {
    id: offer.offerId,
    name: offer.title,
    // #6359: o selo do card de resultados repetido na identidade do hotel.
    ...(offer.agreedRate ? { agreedRateLabel: genUiT(locale, 'hotel.badge.agreedRate') } : {}),
    ...hotelText(offer, data.description),
    photos: hotelPhotos(offer, data.photos).map((photo, index) => ({
      id: `${offer.offerId}-photo-${String(index + 1)}`,
      url: proxyImageUrl(hotelPhotoUrl(photo.url)),
      alt: hotelPhotoAlt(photo.description, offer.title),
    })),
    ...(rating ? { rating } : {}),
    ...(offer.latitude !== undefined && offer.longitude !== undefined
      ? {
          location: { latitude: offer.latitude, longitude: offer.longitude },
          ...(locationLabel ? { locationLabel } : {}),
          ...(mapboxAccessToken
            ? {
                locationMap: {
                  available: true,
                  accessToken: mapboxAccessToken,
                  center: { latitude: offer.latitude, longitude: offer.longitude },
                  anchorLabel: offer.title,
                  fallbackLabel: genUiT(locale, 'hotel.map.aria'),
                },
              }
            : {}),
          ...hotelTravelLabels(offer, anchor, locale),
        }
      : {}),
    amenitySections:
      data.amenities.length === 0
        ? []
        : [
            {
              id: 'hotel-amenities',
              title: genUiT(locale, 'hotelDetails.amenities'),
              amenities: hotelAmenityViews(data.amenities, locale),
            },
          ],
    ...(cancellation ? { cancellation } : {}),
    reviews: reviewViews(data.reviews, locale),
  };
}

export function createHotelDetailsView(
  offer: ChatUiHotelOffer,
  state: HotelDetailsState,
  roomsBlock: HotelRoomsBlock | null,
  options: HotelDetailsViewOptions = {},
): HotelDetailsAdapterView {
  const locale = options.locale ?? 'pt-BR';
  const proxyImageUrl = options.proxyImageUrl ?? ((url: string) => url);
  const roomsView = roomsBlock
    ? createHotelRoomsView(roomsBlock, { locale, proxyImageUrl })
    : undefined;
  const copy = detailsCopy(locale);
  const roomsCopy = roomsView?.copy ?? createHotelRoomsCopy(locale);
  return {
    state: state.phase,
    copy,
    roomsCopy: {
      ...roomsCopy,
      title: genUiT(locale, 'hotelDetails.chooseRoom'),
      selectRateLabel: genUiT(locale, 'hotelDetails.reserveRoom'),
    },
    ...(state.phase === 'loading' ? { message: copy.loadingLabel } : {}),
    /* O título do erro vive em `copy.errorTitleLabel`; aqui só o corpo, senão o
     *  painel repete a frase duas vezes na hierarquia. */
    ...(state.phase === 'error' ? { message: genUiT(locale, 'hotelDetails.errorBody') } : {}),
    ...(state.phase === 'ready'
      ? {
          hotel: readyHotel(offer, state, {
            locale,
            proxyImageUrl,
            todayIso: options.todayIso,
            ...(options.anchor ? { anchor: options.anchor } : {}),
            ...(options.mapboxAccessToken ? { mapboxAccessToken: options.mapboxAccessToken } : {}),
          }),
        }
      : {}),
    ...(roomsView ? roomsPanelProps(roomsView) : {}),
  };
}

/** A lista de quartos no contrato do painel: só as chaves presentes. */
function roomsPanelProps({
  rooms,
  otherRooms,
  selectedRateId,
  initialFilters,
}: ReturnType<typeof createHotelRoomsView>): Pick<
  HotelDetailsAdapterView,
  'rooms' | 'otherRooms' | 'selectedRateId' | 'initialFilters'
> {
  return {
    rooms,
    ...(otherRooms ? { otherRooms } : {}),
    ...(selectedRateId ? { selectedRateId } : {}),
    ...(initialFilters ? { initialFilters } : {}),
  };
}
